/**
 * A short rack stacked with a short wall cabinet (claude-docs/2026-10-30-
 * venus-full-line-design.md, group 5) — so the pair is as tall as the uppers
 * beside it and their top line and bottom line run on. The user (2026-11-01,
 * "ok"): the rack on the uppers' bottom line, where bottles and plates are in
 * reach, and a wall cabinet the line makes above it to the top line; where
 * the line makes no cabinet of that height the rack hangs from the top line
 * with open wall under it, and says why. The rack's record may ask for the
 * cabinet `'below'` it instead, or `'none'`.
 *
 * Kept apart from `racks.js` because the vendor part rule reads that one, and
 * this one reads the line's wall cabinet sizes.
 *
 * Pure; metres where named, else inches.
 */
const { RACKS, rackHeightIn, isRack } = require('./racks');
const { familySizes, unitsOf } = require('./vendorSizes');
const { STOCK_UPPER_HEIGHTS_IN } = require('./upperHeights');

const POSITIONS = Object.freeze(['above', 'below', 'none']);
// a cabinet shorter than this is no cabinet (the shortest any line makes)
const MIN_CABINET_IN = Math.min(...STOCK_UPPER_HEIGHTS_IN);
const SAME_IN = 0.5;

const said = (n) => `${Math.round(n * 8) / 8}"`;

/**
 * The height a line makes a wall cabinet `widthIn` wide that fills
 * `heightIn` exactly, or null: a vendor's from its catalog, any other line's
 * from the stock heights.
 */
function wallCabinetHeightIn(family, widthIn, heightIn) {
  if (heightIn < MIN_CABINET_IN - SAME_IN) return null;
  const made = familySizes(family)
    ? unitsOf(family, 'wall')
        .filter((u) => Math.abs(u.w - widthIn) <= SAME_IN)
        .map((u) => u.h)
    : STOCK_UPPER_HEIGHTS_IN;
  return made.find((h) => Math.abs(h - heightIn) <= SAME_IN) ?? null;
}

/**
 * How a rack `rackId`, `widthIn` wide, stands in an upper band `bandIn` tall
 * — `{ position, rackIn, cabinetIn, reason }`: `position` `'above'` (the
 * cabinet over it, the rack on the bottom line), `'below'` (the cabinet
 * under it, the rack on the top line) or `'none'` (the rack on the top line,
 * open wall under it); `cabinetIn` the cabinet's height, or null; `reason`
 * why a cabinet asked for is not there. The wine cubes stand the band's
 * height and take none. Null when the rack does not fit at all.
 *
 * @param {Object} family  the run's line
 * @param {'above'|'below'|'none'|null} asked  the rack's record; null = above
 */
function rackStackPlan(family, rackId, widthIn, bandIn, asked = null) {
  const rackIn = rackHeightIn(rackId, widthIn, bandIn);
  if (rackIn == null) return null;
  const none = (reason = null) => ({
    position: 'none',
    rackIn,
    cabinetIn: null,
    reason,
  });
  if (RACKS[rackId].heightsIn) return none();
  const want = POSITIONS.includes(asked) ? asked : 'above';
  const leftIn = bandIn - rackIn;
  if (want === 'none' || leftIn < SAME_IN) return none();
  const cabinetIn = wallCabinetHeightIn(family, widthIn, leftIn);
  if (cabinetIn == null)
    return none(
      `${family?.name || 'This line'} makes no wall cabinet ${widthIn}" wide and ${said(leftIn)} tall, so the rack hangs from the top line with open wall under it`
    );
  return { position: want, rackIn, cabinetIn, reason: null };
}

// the fronts the cabinet stacked with a rack can wear: doors (the default),
// glass doors, or none — an open shelf
const FRONTS = Object.freeze(['doors', 'glass', 'open']);

/** The front a rack's record asks its stacked cabinet to wear. */
function rackCabinetFrontOf(record) {
  return FRONTS.includes(record?.rackCabinetFront)
    ? record.rackCabinetFront
    : 'doors';
}

/**
 * The rack a cabinet stacked with one belongs to — the one it names
 * (`rackOf`, stamped by the fill) — or null for any other object.
 */
function rackOfCompanion(cabinet, objects) {
  if (!cabinet?.category?.rackCompanion || !cabinet.rackOf) return null;
  return (
    (objects || []).find((o) => o.id === cabinet.rackOf && isRack(o)) ?? null
  );
}

module.exports = {
  RACK_CABINET_POSITIONS: POSITIONS,
  RACK_CABINET_FRONTS: FRONTS,
  rackStackPlan,
  rackCabinetFrontOf,
  rackOfCompanion,
};
